import { DoubleMediaElementSchema } from '../../schema/pageElements.val';
import HotspotImage from '../HotspotImage';
import AutoplayVideo from '../AutoplayVideo';
import { Image, RichText } from '@valbuild/next';
import Body from '../Body/Body';

type MediaProps =
  | {
      type: 'image';
      content: Image;
    }
  | {
      type: 'video';
      content: VideoFile;
      autoPlay?: boolean | null;
    };

interface VideoFile {
  url: string;
  metadata?: {
    mimeType?: string;
  };
}

const renderMediaItem = (media: MediaProps, aspectRatio: string) => {
  if (!media) return null;

  if (media.type === 'video') {
    return (
      <AutoplayVideo videoFile={media.content} setAspectRatio={aspectRatio} />
    );
  } else {
    return (
      <HotspotImage
        image={media.content}
        setAspectRatio={aspectRatio}
        sizes={'(max-width: 768px) 100vw, (max-width: 1920px) 50vw, 1872px'}
      />
    );
  }
};

interface Props {
  media: [MediaProps, MediaProps];
  title?: string;
  text?: RichText<{
    h1: true;
    h2: true;
    h3: true;
    h4: true;
    h5: true;
    h6: true;
    ul: true;
    ol: true;
    a: true;
    img: true;
    bold: true;
    italic: true;
    lineThrough: true;
  }>;
  layout: DoubleMediaElementSchema['layout'];
}

const DoubleMediaElement = ({ media, title, text, layout }: Props) => {
  const wrapperCass =
    'w-full flex flex-col tablet:flex-row laptop:flex-row gap-24';
  const layoutConfig = {
    'square-tall': {
      first: 'aspect-square',
      second: 'aspect-[1/1.6]',
    },
    'tall-square': {
      first: 'aspect-[1/1.6]',
      second: 'aspect-square',
    },
    'square-square': {
      first: 'aspect-square',
      second: 'aspect-square',
    },
  } as const;
  const cfg = layoutConfig[layout];

  const titleElement = title ? <h3 className="text-3xl">{title}</h3> : null;

  const textElement =
    typeof text === 'string' ? (
      <p className="text-lg tablet:text-xl">{text}</p>
    ) : (
      <Body richText={text} />
    );

  return (
    <div className={wrapperCass}>
      <div className="flex-1 w-full flex flex-col gap-24">
        <div className={`w-full relative ${cfg.first}`}>
          {renderMediaItem(media[0], cfg.first)}
        </div>
        <div className="flex flex-col gap-16">
          {(layout === 'square-tall' || layout === 'square-square') &&
            titleElement}
          {(layout === 'square-tall' || layout === 'square-square') &&
            textElement}
        </div>
      </div>

      <div className="flex-1 w-full flex flex-col gap-24">
        <div className={`w-full relative ${cfg.second}`}>
          {renderMediaItem(media[1], cfg.second)}
        </div>
        <div className="flex flex-col gap-16">
          {layout === 'tall-square' && titleElement}
          {layout === 'tall-square' && textElement}
        </div>
      </div>
    </div>
  );
};

export default DoubleMediaElement;
